import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { useAuthenticatedClient } from '@/api/queries'
import { useAnalyticsBreakdown, type AnalyticsBreakdownRow } from '@/api/analytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { BOOKED_COLOR, num, REVENUE_COLOR, windowStart, WINDOW_MONTHS } from './model'
import { Figure } from './trends'

const TOP = 8

function useLeadTimes() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['quoted-vs-actual', 'home', WINDOW_MONTHS],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quoted-vs-actual', {
        params: { query: { group: 'manufacturer', months: WINDOW_MONTHS, sort: 'pos', page_size: TOP } },
      })
      if (error) throw error
      return data!
    },
  })
}

function CardTitleLink({ to, children }: { to: '/quoted-vs-actual' | '/analytics'; children: string }) {
  return (
    <CardTitle className="text-base">
      <Link to={to} className="inline-flex items-center gap-1 hover:underline">
        {children}
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    </CardTitle>
  )
}

function days(value: number | null | undefined) {
  if (value == null) return '-'
  const rounded = Number(value.toFixed(1))
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`
}

export function LeadTimesCard() {
  const { data, isLoading } = useLeadTimes()
  const totals = data?.totals.lead_time
  const rows = (data?.rows ?? []).filter(
    (row) => row.manufacturer && row.lead_time.quoted_days != null && row.lead_time.actual_ship_days != null,
  )
  const scale = Math.max(
    1,
    ...rows.flatMap((row) => [row.lead_time.quoted_days ?? 0, row.lead_time.actual_ship_days ?? 0]),
  )
  const gap = totals?.gap_days
  return (
    <Card>
      <CardHeader>
        <CardTitleLink to="/quoted-vs-actual">Lead times: quoted vs actual</CardTitleLink>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
              <Figure label="Quoted to ship" value={days(totals?.quoted_days)} />
              <Figure label="Actually shipped" value={days(totals?.actual_ship_days)} />
              {gap != null && gap !== 0 ? (
                <Figure
                  label={gap > 0 ? 'Later than quoted' : 'Sooner than quoted'}
                  value={days(Math.abs(gap))}
                  tone={gap > 0 ? 'text-red-700' : 'text-emerald-700'}
                />
              ) : null}
              <Figure label="Delivered" value={days(totals?.actual_delivery_days)} />
            </div>
            <div>
              <div className="mb-2 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full border-2 bg-white" style={{ borderColor: BOOKED_COLOR }} />
                  Quoted
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: REVENUE_COLOR }} />
                  Shipped
                </span>
                <span className="ml-auto">Business days, busiest manufacturers</span>
              </div>
              <ul className="space-y-2">
                {rows.map((row) => {
                  const quoted = row.lead_time.quoted_days ?? 0
                  const actual = row.lead_time.actual_ship_days ?? 0
                  const low = (Math.min(quoted, actual) / scale) * 100
                  const high = (Math.max(quoted, actual) / scale) * 100
                  const late = actual > quoted
                  return (
                    <li key={row.key} className="grid grid-cols-[minmax(0,9rem)_1fr_4.5rem] items-center gap-3 text-sm">
                      <Link
                        to="/manufacturers/$manuId"
                        params={{ manuId: row.manufacturer!.uuid }}
                        className="truncate hover:underline"
                        title={row.manufacturer!.name}
                      >
                        {row.manufacturer!.name}
                      </Link>
                      <div
                        className="relative h-4"
                        aria-label={`Quoted ${days(quoted)}, shipped in ${days(actual)}`}
                        role="img"
                      >
                        <div className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-border" />
                        <div
                          className="absolute top-1/2 h-0.5 -translate-y-1/2 bg-muted-foreground/40"
                          style={{ left: `${low}%`, width: `${high - low}%` }}
                        />
                        <span
                          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-white"
                          style={{ left: `${(quoted / scale) * 100}%`, borderColor: BOOKED_COLOR }}
                        />
                        <span
                          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                          style={{ left: `${(actual / scale) * 100}%`, backgroundColor: REVENUE_COLOR }}
                        />
                      </div>
                      <span
                        className={cn(
                          'text-right text-xs tabular-nums',
                          late ? 'text-red-700' : 'text-muted-foreground',
                        )}
                      >
                        {actual === quoted ? 'On time' : `${late ? '+' : '−'}${Number(Math.abs(actual - quoted).toFixed(1))}d`}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function TopList({
  title,
  rows,
  isLoading,
  link,
}: {
  title: string
  rows: AnalyticsBreakdownRow[]
  isLoading: boolean
  link: (row: AnalyticsBreakdownRow) => React.ReactNode
}) {
  const max = Math.max(1, ...rows.map((row) => num(row.revenue)))
  return (
    <Card>
      <CardHeader>
        <CardTitleLink to="/analytics">{title}</CardTitleLink>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales in the last 12 months</p>
        ) : (
          <ul className="space-y-3">
            <li className="grid grid-cols-[minmax(0,13rem)_1fr_5.5rem_3.5rem] gap-3 text-xs text-muted-foreground">
              <span />
              <span />
              <span className="text-right">Revenue</span>
              <span className="text-right">Margin</span>
            </li>
            {rows.map((row) => {
              const margin = row.margin == null ? null : Number(row.margin)
              return (
                <li key={row.key} className="grid grid-cols-[minmax(0,13rem)_1fr_5.5rem_3.5rem] items-center gap-3 text-sm">
                  <span className="truncate" title={row.detail ? `${row.detail} › ${row.label}` : row.label}>
                    {link(row)}
                  </span>
                  <div className="h-2.5 rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(num(row.revenue) / max) * 100}%`, backgroundColor: REVENUE_COLOR }}
                    />
                  </div>
                  <span className="text-right tabular-nums">{formatMoney(row.revenue)}</span>
                  <span className={cn('text-right text-xs tabular-nums', margin != null && margin < 0 && 'text-red-700')}>
                    {formatPercent(margin)}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

export function TopManufacturers() {
  const { data, isLoading } = useAnalyticsBreakdown({ dimension: 'manufacturer', start: windowStart(), limit: TOP + 1 })
  const rows = (data?.rows ?? []).filter((row) => row.uuid).slice(0, TOP)
  return (
    <TopList
      title="Top manufacturers"
      rows={rows}
      isLoading={isLoading}
      link={(row) => (
        <Link to="/manufacturers/$manuId" params={{ manuId: row.uuid! }} className="hover:underline">
          {row.label}
        </Link>
      )}
    />
  )
}

export function TopCategories() {
  const { data, isLoading } = useAnalyticsBreakdown({
    dimension: 'category',
    start: windowStart(),
    limit: TOP,
    category_level: 2,
  })
  return (
    <TopList
      title="Top store categories"
      rows={data?.rows ?? []}
      isLoading={isLoading}
      link={(row) => row.label}
    />
  )
}
